// ================================================================
// Email API — connections + received Gmail messages
// Same apiRequest() pattern as core/org.js
// ================================================================
import { apiRequest } from './apiClient.js';

export function listConnections() { return apiRequest('/api/connections'); }

// Welche Verbindungen sind ein POSTFACH? Reihenfolge = Vorrang, identisch zur
// MAIL_PROVIDERS-Liste im Backend (connections/mail-sender.service.ts).
// Wichtig, weil unter /api/connections auch Kalender-Verbindungen liegen:
// «nimm einfach die erste» zeigte sonst einen Outlook-KALENDER als Postfach an.
export const MAIL_PROVIDERS = ['GOOGLE', 'MICROSOFT', 'IMAP'];

/** Das Postfach, mit dem die App arbeiten soll (oder null). */
export function pickMailConnection(connections) {
  const list = connections || [];
  for (const p of MAIL_PROVIDERS) {
    const hit = list.find((c) => c && c.provider === p);
    if (hit) return hit;
  }
  return null;
}

export function listGmailMessages(id) {
  return apiRequest('/api/connections/' + encodeURIComponent(id) + '/gmail/messages');
}

// Sync: ایمیل‌ها را از Gmail بگیر و در DB ذخیره کن.
// این نسخه همیشه sync می‌کند — برای وقتی که کاربر خودش خواسته (کشیدن صفحه).
export function syncGmail(id) {
  return apiRequest('/api/connections/' + encodeURIComponent(id) + '/gmail/sync', { method: 'POST' });
}

// «تازه‌سازی هنگام باز شدن صفحه»: همه‌ی صندوق‌های سازمان، ولی سرور خودش ترمز
// دارد و اگر تازه sync شده باشد کاری نمی‌کند. بدون نیاز به شناسه‌ی اتصال —
// هم صفحه‌ی ایمیل و هم صفحه‌ی لیدها همین یکی را صدا می‌زنند.
export function refreshGmail() {
  return apiRequest('/api/connections/gmail/refresh', { method: 'POST' });
}

// Send: یک ایمیل بفرست (به مشتری) — outbound interaction خودکار ثبت می‌شود.
// lang = زبانِ ارسال ('auto' = سرور از آخرین ایمیلِ رسیده تشخیص می‌دهد)؛
// uiLang = زبان UI (متنِ خودِ کاربر به‌عنوان کشِ ترجمه ذخیره می‌شود)؛
// tone = بازنویسیِ لحنِ متنِ تایپی ('short'|'friendly'|'polite').
// Mails EINES Kunden (org-scoped auf dem Server) — die Kundenkarte holt sich
// daraus die threadId der letzten Mail, damit ihre Antwort im selben Thread
// bleibt.
export function listContactEmails(contactId, box = 'inbox', limit = 5) {
  const p = new URLSearchParams({ contactId, box, limit: String(limit) });
  return apiRequest('/api/emails?' + p.toString());
}

export function sendGmail(id, to, subject, body, threadId, lang, uiLang, tone) {
  return apiRequest('/api/connections/' + encodeURIComponent(id) + '/gmail/send', {
    method: 'POST',
    body: JSON.stringify({
      to, subject, body,
      ...(threadId ? { threadId } : {}),
      ...(lang ? { lang, uiLang } : {}),
      ...(tone ? { tone } : {}),
    }),
  });
}

// تنظیماتِ ارسال برای یک مخاطب (زبان + لحن) — روی Contact ذخیره می‌شود.
// prefs: { language?, remember?, tone?, toneRemember? } — tone='ask' یعنی پاک کن.
export function setEmailSendPrefs(address, prefs) {
  return apiRequest('/api/emails/send-prefs', {
    method: 'POST',
    body: JSON.stringify({ address, ...prefs }),
  });
}

// خواندنِ تنظیمات ارسال یک آدرس (برای «ایمیل جدید»: فقط بار اول بپرس)
export function getEmailSendPrefs(address) {
  return apiRequest('/api/emails/send-prefs?address=' + encodeURIComponent(address));
}

// ایمیل‌های ذخیره‌شده از DB (با فیلتر دسته). box: 'inbox' (پیش‌فرض) یا 'sent'
export function listStoredEmails(category, box) {
  const params = new URLSearchParams();
  if (category && category !== 'all') params.set('category', category);
  if (box && box !== 'inbox') params.set('box', box);
  const q = params.toString();
  return apiRequest('/api/emails' + (q ? '?' + q : ''));
}

// ── پیش‌نویس‌ها (تب «Drafts») ──
export function listEmailDrafts() {
  return apiRequest('/api/emails/drafts');
}

// ذخیره/upsert پیش‌نویس — {id?, threadId?, emailId?, toEmail?, subject?, body}
export function saveEmailDraft(draft) {
  return apiRequest('/api/emails/drafts', {
    method: 'POST',
    body: JSON.stringify(draft),
  });
}

export function deleteEmailDraft(id) {
  return apiRequest('/api/emails/drafts/' + encodeURIComponent(id), { method: 'DELETE' });
}

/**
 * Gelesen/ungelesen setzen. Antwort enthält den neuen Ungelesen-Zähler der
 * Org, damit die Navigation nicht zusätzlich nachladen muss.
 */
export function setEmailRead(id, read) {
  return apiRequest('/api/emails/' + encodeURIComponent(id) + '/read', {
    method: 'PATCH',
    body: JSON.stringify({ read }),
  });
}

// تحلیل یک ایمیل با GPT
export function analyzeEmail(id) {
  return apiRequest('/api/emails/' + encodeURIComponent(id) + '/analyze', { method: 'POST' });
}

// حذف یک ایمیل از دیتابیس
export function deleteEmail(id) {
  return apiRequest('/api/emails/' + encodeURIComponent(id), { method: 'DELETE' });
}

// گرفتن یک ایمیل کامل (با body). با lang (زبان UI) ترجمه هم برمی‌گردد
// (translatedBody/translatedLang — سرور کش می‌کند).
export function getEmail(id, lang) {
  const q = lang ? '?lang=' + encodeURIComponent(lang) : '';
  return apiRequest('/api/emails/' + encodeURIComponent(id) + q);
}

// کل رشتهٔ گفتگو (هر دو جهت) — ترجمه‌ها فقط از کش؛ کلاینت پیام‌به‌پیام
// با getEmail(id, lang) تکمیل می‌کند (برای نمایش پروگرس).
export function getEmailThread(id) {
  return apiRequest('/api/emails/' + encodeURIComponent(id) + '/thread');
}

// پیشنهاد به مدیر (GPT — موقتی)
export function suggestEmail(id) {
  return apiRequest('/api/emails/' + encodeURIComponent(id) + '/suggest', { method: 'POST' });
}

// ── پیوست‌ها (Chat-Verlauf) ──
import { getToken } from './auth.js';
import { readErrorMessage } from './apiClient.js';

// دانلود محتوای یک پیوست به‌صورت object URL (call-site باید revokeObjectURL کند)
export async function fetchEmailAttachment(emailId, attachmentId) {
  const token = getToken();
  const res = await fetch(
    '/api/emails/' + encodeURIComponent(emailId) + '/attachments/' + encodeURIComponent(attachmentId),
    { headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) } },
  );
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch { /* binäre Antwort */ }
    const err = new Error(readErrorMessage(body, res.status));
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ارسال ایمیل با فایل‌های ضمیمه (multipart) — files: File[]
export async function sendGmailWithFiles(id, { to, subject, body, threadId, files, lang, uiLang, tone }) {
  const token = getToken();
  const fd = new FormData();
  fd.append('to', to);
  fd.append('subject', subject);
  fd.append('body', body);
  if (threadId) fd.append('threadId', threadId);
  if (lang) { fd.append('lang', lang); if (uiLang) fd.append('uiLang', uiLang); }
  if (tone) fd.append('tone', tone);
  for (const f of files) fd.append('files', f, f.name);
  const res = await fetch('/api/connections/' + encodeURIComponent(id) + '/gmail/send-with-files', {
    method: 'POST',
    headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: fd,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(readErrorMessage(json, res.status));
    err.status = res.status;
    throw err;
  }
  return json;
}

// فوروارد یک ایمیل به آدرس دیگر (با پیوست‌ها؛ متن آلمانی، ترد جدید)
export function forwardEmail(id, to, note) {
  return apiRequest('/api/emails/' + encodeURIComponent(id) + '/forward', {
    method: 'POST',
    body: JSON.stringify({ to, ...(note ? { note } : {}) }),
  });
}

// پیشنهادهای پاسخ با هوش مصنوعی — یک درخواست، همهٔ لحن‌ها (هر کدام ۲ تا).
// پاسخ: { tones: { short: [...], friendly: [...], polite: [...] } }
export function suggestEmailReplies(id, lang) {
  return apiRequest('/api/emails/' + encodeURIComponent(id) + '/suggest-replies', {
    method: 'POST',
    body: JSON.stringify({ lang }),
  });
}
